/**
 * CaseFolderManager - Saksmapper med multi-opplasting, tidslinje og kryssanalyse
 * Vises i alle private modulers oversikt-fane
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  FolderPlus, Upload, FileText, Trash2, Loader2, Play, Download,
  ChevronDown, ChevronUp, X, Plus, Clock, AlertTriangle,
  CheckCircle, XCircle, Info, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const LABELS = ["Kontrakt", "E-post", "Vedtak", "Brev", "SMS", "Legeerklaring", "Faktura", "Kvittering", "Rapport", "Annet"];

export function CaseFolderManager({ moduleId }) {
  const { user, token } = useAuth();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [activeFolder, setActiveFolder] = useState(null);

  useEffect(() => {
    if (user?.id) fetchFolders();
  }, [moduleId, user?.id]);

  const fetchFolders = async () => {
    try {
      const res = await fetch(`${API}/api/case-folders/list/${moduleId}?user_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFolders(data.folders || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleCreated = (folder) => {
    setFolders(prev => [folder, ...prev]);
    setCreateOpen(false);
    setActiveFolder(folder.id);
  };

  const handleDelete = async (folderId) => {
    if (!confirm('Slette hele saksmappen?')) return;
    await fetch(`${API}/api/case-folders/${folderId}?user_id=${user.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    setFolders(prev => prev.filter(f => f.id !== folderId));
    if (activeFolder === folderId) setActiveFolder(null);
    toast.success('Saksmappe slettet');
  };

  if (loading) return <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--copper)]" /></div>;

  return (
    <div className="space-y-4" data-testid="case-folder-manager">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[var(--text-main)]">Saksmapper</h3>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--copper)]/10 text-[var(--copper)] border border-[var(--copper)]/30 rounded-lg hover:bg-[var(--copper)]/20"
          data-testid="create-folder-btn"
        >
          <FolderPlus className="w-3.5 h-3.5" /> Ny saksmappe
        </button>
      </div>

      {createOpen && (
        <CreateFolderForm moduleId={moduleId} onCreated={handleCreated} onCancel={() => setCreateOpen(false)} />
      )}

      {folders.length === 0 && !createOpen ? (
        <div className="text-center py-8 bg-[var(--bg-surface)] rounded-xl border border-[var(--border-subtle)]">
          <FolderPlus className="w-10 h-10 text-[var(--text-muted)] mx-auto mb-3 opacity-40" />
          <p className="text-sm text-[var(--text-muted)]">Ingen saksmapper enna</p>
          <p className="text-xs text-[var(--text-muted)] mt-1 opacity-70">
            Opprett en saksmappe for a laste opp flere dokumenter og fa kryssanalyse
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {folders.map(f => (
            <FolderCard
              key={f.id}
              folder={f}
              isActive={activeFolder === f.id}
              onToggle={() => setActiveFolder(activeFolder === f.id ? null : f.id)}
              onDelete={() => handleDelete(f.id)}
              onUpdated={fetchFolders}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function CreateFolderForm({ moduleId, onCreated, onCancel }) {
  const { user, token } = useAuth();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/case-folders/create`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: (() => { const fd = new FormData(); fd.append('name', name); fd.append('module_id', moduleId); fd.append('user_id', user.id); fd.append('description', desc); return fd; })()
      });
      const data = await res.json();
      if (data.success) onCreated(data.folder);
    } catch (e) { toast.error('Kunne ikke opprette mappe'); }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-[var(--bg-surface)] rounded-xl border border-[var(--copper)]/30 space-y-3">
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Navn pa saksmappe (f.eks. Husleietvist 2026)"
        className="w-full px-3 py-2 bg-[#0F1419] border border-[var(--border-subtle)] rounded-lg text-white placeholder-[var(--text-muted)] text-sm focus:outline-none focus:border-[var(--copper)]"
        data-testid="folder-name-input" autoFocus />
      <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Kort beskrivelse (valgfritt)"
        className="w-full px-3 py-2 bg-[#0F1419] border border-[var(--border-subtle)] rounded-lg text-white placeholder-[var(--text-muted)] text-sm focus:outline-none focus:border-[var(--copper)]" />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-[var(--text-muted)]">Avbryt</button>
        <button type="submit" disabled={!name.trim() || saving}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--copper)] text-white rounded-lg text-sm disabled:opacity-50"
          data-testid="folder-create-submit">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5" />} Opprett
        </button>
      </div>
    </form>
  );
}


function FolderCard({ folder, isActive, onToggle, onDelete, onUpdated }) {
  const { user, token } = useAuth();
  const [uploadMode, setUploadMode] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detailFolder, setDetailFolder] = useState(folder);

  const docCount = detailFolder.documents?.length || 0;
  const hasAnalysis = !!detailFolder.analysis;

  const refreshFolder = async () => {
    const res = await fetch(`${API}/api/case-folders/${folder.id}?user_id=${user.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) { const d = await res.json(); setDetailFolder(d.folder); }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const fd = new FormData(); fd.append('user_id', user.id);
      const res = await fetch(`${API}/api/case-folders/${folder.id}/analyze`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (res.ok) {
        const data = await res.json();
        setDetailFolder(prev => ({ ...prev, analysis: data.analysis, timeline: data.analysis.tidslinje, status: 'analyzed' }));
        toast.success('Kryssanalyse fullfort!');
      } else {
        const err = await res.json(); toast.error(err.detail || 'Analyse feilet');
      }
    } catch (e) { toast.error('Analyse feilet'); }
    setAnalyzing(false);
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/case-folders/${folder.id}/export-pdf?user_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = `RettVerk_Saksmappe_${folder.name.replace(/\s/g, '_')}.pdf`;
        a.click(); URL.revokeObjectURL(url);
        toast.success('PDF eksportert!');
      }
    } catch (e) { toast.error('Eksport feilet'); }
    setExporting(false);
  };

  const removeDoc = async (docId) => {
    await fetch(`${API}/api/case-folders/${folder.id}/documents/${docId}?user_id=${user.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    setDetailFolder(prev => ({ ...prev, documents: prev.documents.filter(d => d.id !== docId) }));
  };

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl overflow-hidden" data-testid={`folder-${folder.id}`}>
      {/* Header */}
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 hover:bg-[#0F1419]/50 text-left">
        <div className="p-2 rounded-lg bg-[var(--copper)]/15">
          <FileText className="w-4 h-4 text-[var(--copper)]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--text-main)] truncate">{folder.name}</p>
          <p className="text-xs text-[var(--text-muted)]">
            {docCount} dokument{docCount !== 1 ? 'er' : ''} {hasAnalysis ? '| Analysert' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasAnalysis && (
            <span className="text-[10px] px-1.5 py-0.5 bg-[#22c55e]/15 text-[#22c55e] rounded">ANALYSERT</span>
          )}
          {isActive ? <ChevronUp className="w-4 h-4 text-[var(--text-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" />}
        </div>
      </button>

      {/* Expanded content */}
      {isActive && (
        <div className="border-t border-[var(--border-subtle)] p-4 space-y-4">
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setUploadMode(!uploadMode)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[var(--copper)]/10 text-[var(--copper)] border border-[var(--copper)]/30 rounded-lg hover:bg-[var(--copper)]/20"
              data-testid="folder-upload-btn">
              <Upload className="w-3.5 h-3.5" /> Legg til dokumenter
            </button>
            {docCount > 0 && (
              <button onClick={runAnalysis} disabled={analyzing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#22c55e]/10 text-[#22c55e] border border-[#22c55e]/30 rounded-lg hover:bg-[#22c55e]/20 disabled:opacity-50"
                data-testid="folder-analyze-btn">
                {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                {analyzing ? 'Analyserer...' : 'Kjor kryssanalyse'}
              </button>
            )}
            {hasAnalysis && (
              <button onClick={exportPdf} disabled={exporting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-500/10 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 disabled:opacity-50"
                data-testid="folder-export-btn">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Eksporter PDF
              </button>
            )}
            <button onClick={onDelete} className="ml-auto p-1.5 rounded hover:bg-red-500/20 text-[var(--text-muted)] hover:text-red-400">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Upload section */}
          {uploadMode && (
            <BulkUploadForm folderId={folder.id} onDone={() => { setUploadMode(false); refreshFolder(); }} />
          )}

          {/* Document list */}
          {docCount > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-[var(--text-muted)] uppercase">Dokumenter i mappen</p>
              {detailFolder.documents.map(d => (
                <div key={d.id} className="flex items-center gap-2 px-3 py-2 bg-[#0F1419] rounded-lg">
                  <FileText className="w-3.5 h-3.5 text-[var(--copper)] flex-shrink-0" />
                  <span className="text-sm text-[var(--text-main)] truncate flex-1">{d.filename}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-[var(--copper)]/15 text-[var(--copper)] rounded flex-shrink-0">{d.label}</span>
                  {d.description && <span className="text-xs text-[var(--text-muted)] truncate max-w-[120px]">{d.description}</span>}
                  <button onClick={() => removeDoc(d.id)} className="p-1 hover:text-red-400 text-[var(--text-muted)]"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}

          {/* Analysis Results */}
          {hasAnalysis && <AnalysisView analysis={detailFolder.analysis} />}
        </div>
      )}
    </div>
  );
}


function BulkUploadForm({ folderId, onDone }) {
  const { user, token } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (newFiles) => {
    const items = Array.from(newFiles).map(f => ({
      file: f, label: 'Annet', description: ''
    }));
    setFiles(prev => [...prev, ...items]);
  };

  const updateFile = (idx, field, value) => {
    setFiles(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('user_id', user.id);
    files.forEach(f => fd.append('files', f.file));
    fd.append('labels', JSON.stringify(files.map(f => f.label)));
    fd.append('descriptions', JSON.stringify(files.map(f => f.description)));

    try {
      const res = await fetch(`${API}/api/case-folders/${folderId}/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.uploaded_count} dokument(er) lastet opp!`);
        onDone();
      }
    } catch (e) { toast.error('Opplasting feilet'); }
    setUploading(false);
  };

  return (
    <div className="p-4 bg-[#0F1419] rounded-xl border border-[var(--border-subtle)] space-y-3">
      {/* Drop zone */}
      <div onClick={() => inputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-[var(--border-subtle)] rounded-lg p-4 text-center cursor-pointer hover:border-[var(--copper)]/50">
        <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.jpg,.jpeg,.png" className="hidden"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
        <Upload className="w-6 h-6 text-[var(--text-muted)] mx-auto mb-1" />
        <p className="text-sm text-[var(--text-muted)]">Dra filer hit eller klikk for a velge</p>
        <p className="text-xs text-[var(--text-muted)] opacity-60">PDF, DOCX, JPG, PNG - maks 15 MB per fil</p>
      </div>

      {/* File list with labels */}
      {files.map((f, i) => (
        <div key={i} className="flex items-center gap-2 flex-wrap">
          <FileText className="w-4 h-4 text-[var(--copper)] flex-shrink-0" />
          <span className="text-sm text-white truncate max-w-[180px]">{f.file.name}</span>
          <select value={f.label} onChange={e => updateFile(i, 'label', e.target.value)}
            className="px-2 py-1 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded text-xs text-white">
            {LABELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <input value={f.description} onChange={e => updateFile(i, 'description', e.target.value)}
            placeholder="Kort beskrivelse..."
            className="flex-1 min-w-[120px] px-2 py-1 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded text-xs text-white placeholder-[var(--text-muted)]" />
          <button onClick={() => removeFile(i)} className="p-1 hover:text-red-400 text-[var(--text-muted)]"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}

      {files.length > 0 && (
        <div className="flex justify-end gap-2">
          <button onClick={onDone} className="px-3 py-1.5 text-sm text-[var(--text-muted)]">Avbryt</button>
          <button onClick={handleUpload} disabled={uploading}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--copper)] text-white rounded-lg text-sm disabled:opacity-50"
            data-testid="bulk-upload-submit">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Last opp {files.length} fil{files.length !== 1 ? 'er' : ''}
          </button>
        </div>
      )}
    </div>
  );
}


function AnalysisView({ analysis }) {
  const [section, setSection] = useState('tidslinje');

  const tabs = [
    { id: 'tidslinje', label: 'Tidslinje', icon: Clock },
    { id: 'samsvarssjekk', label: 'Samsvarssjekk', icon: AlertTriangle },
    { id: 'vurdering', label: 'Vurdering', icon: FileText },
  ];

  return (
    <div className="space-y-3" data-testid="analysis-view">
      <div className="flex gap-1 bg-[#0F1419] rounded-lg p-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setSection(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              section === t.id ? 'bg-[var(--copper)] text-white' : 'text-[var(--text-muted)] hover:text-white'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {section === 'tidslinje' && <TimelineView events={analysis.tidslinje || []} />}
      {section === 'samsvarssjekk' && <ConsistencyView checks={analysis.samsvarssjekk || []} />}
      {section === 'vurdering' && <AssessmentView analysis={analysis} />}
    </div>
  );
}


function TimelineView({ events }) {
  if (!events.length) return <p className="text-sm text-[var(--text-muted)] py-4 text-center">Ingen hendelser funnet</p>;

  const typeColors = {
    hendelse: 'bg-blue-500', dokument: 'bg-[var(--copper)]',
    frist: 'bg-red-500', varsel: 'bg-amber-500'
  };

  return (
    <div className="relative pl-6 space-y-4 py-2" data-testid="timeline">
      <div className="absolute left-[11px] top-4 bottom-4 w-0.5 bg-[var(--border-subtle)]" />
      {events.map((ev, i) => (
        <div key={i} className="relative">
          <div className={`absolute left-[-17px] top-1.5 w-3 h-3 rounded-full border-2 border-[var(--bg-surface)] ${typeColors[ev.type] || 'bg-[var(--copper)]'}`} />
          <div className="bg-[#0F1419] rounded-lg p-3 border border-[var(--border-subtle)]">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-[var(--copper)]">{ev.dato || 'Ukjent dato'}</span>
              <span className="text-[10px] px-1.5 py-0.5 bg-[var(--border-subtle)] text-[var(--text-muted)] rounded">{ev.type}</span>
            </div>
            <p className="text-sm font-medium text-[var(--text-main)]">{ev.tittel}</p>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">{ev.beskrivelse}</p>
            {ev.dokument && <p className="text-[10px] text-[var(--copper)] mt-1">Kilde: {ev.dokument}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}


function ConsistencyView({ checks }) {
  if (!checks.length) return <p className="text-sm text-[var(--text-muted)] py-4 text-center">Ingen funn</p>;

  const icons = { motstrid: XCircle, mangel: AlertTriangle, bekreftelse: CheckCircle };
  const colors = { kritisk: 'text-red-400 bg-red-500/10', viktig: 'text-amber-400 bg-amber-500/10', merknad: 'text-blue-400 bg-blue-500/10' };

  return (
    <div className="space-y-2" data-testid="consistency-checks">
      {checks.map((c, i) => {
        const Icon = icons[c.type] || Info;
        const color = colors[c.alvorlighetsgrad] || colors.merknad;
        return (
          <div key={i} className={`p-3 rounded-lg border border-[var(--border-subtle)] ${color.split(' ')[1]}`}>
            <div className="flex items-start gap-2">
              <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${color.split(' ')[0]}`} />
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-xs font-semibold uppercase ${color.split(' ')[0]}`}>{c.alvorlighetsgrad}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">{c.type}</span>
                </div>
                <p className="text-sm text-[var(--text-main)]">{c.beskrivelse}</p>
                {c.dokumenter?.length > 0 && (
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">Dokumenter: {c.dokumenter.join(', ')}</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function AssessmentView({ analysis }) {
  return (
    <div className="space-y-4" data-testid="assessment-view">
      {analysis.helhetsvurdering && (
        <div className="p-3 bg-[#0F1419] rounded-lg border border-[var(--border-subtle)]">
          <p className="text-xs font-medium text-[var(--copper)] mb-1 uppercase">Helhetsvurdering</p>
          <p className="text-sm text-[var(--text-main)] leading-relaxed">{analysis.helhetsvurdering}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {analysis.styrker?.length > 0 && (
          <div className="p-3 bg-[#22c55e]/5 rounded-lg border border-[#22c55e]/20">
            <p className="text-xs font-medium text-[#22c55e] mb-2 uppercase">Styrker</p>
            {analysis.styrker.map((s, i) => (
              <div key={i} className="flex items-start gap-1.5 mb-1">
                <CheckCircle className="w-3 h-3 text-[#22c55e] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-[var(--text-main)]">{s}</span>
              </div>
            ))}
          </div>
        )}
        {analysis.svakheter?.length > 0 && (
          <div className="p-3 bg-red-500/5 rounded-lg border border-red-500/20">
            <p className="text-xs font-medium text-red-400 mb-2 uppercase">Svakheter</p>
            {analysis.svakheter.map((s, i) => (
              <div key={i} className="flex items-start gap-1.5 mb-1">
                <XCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-[var(--text-main)]">{s}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {analysis.manglende_dokumentasjon?.length > 0 && (
        <div className="p-3 bg-amber-500/5 rounded-lg border border-amber-500/20">
          <p className="text-xs font-medium text-amber-400 mb-2 uppercase">Manglende dokumentasjon</p>
          {analysis.manglende_dokumentasjon.map((s, i) => (
            <div key={i} className="flex items-start gap-1.5 mb-1">
              <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-[var(--text-main)]">{s}</span>
            </div>
          ))}
        </div>
      )}

      {analysis.anbefaling && (
        <div className="p-3 bg-[var(--copper)]/5 rounded-lg border border-[var(--copper)]/20">
          <p className="text-xs font-medium text-[var(--copper)] mb-1 uppercase">Anbefalt neste steg</p>
          <p className="text-sm text-[var(--text-main)] leading-relaxed">{analysis.anbefaling}</p>
        </div>
      )}
    </div>
  );
}

export default CaseFolderManager;
